import { useCurrentClient } from "@/features/clients/hooks/use-clients";
import { websocketBus } from "@/features/websockets";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

/**
 * Change event format from backend
 * Example: { type: "changes", t: "sessions", cid: "client-123", ids: ["id1", "id2"], op: "upsert" }
 */
interface ChangeEvent {
  type: "changes";
  t: string; // table name
  cid: string; // client_id
  ids: string[]; // array of entity IDs (debounced and merged over 500ms)
  op: "upsert" | "delete"; // operation type
}

/**
 * Hook for client real-time database change notifications
 * Automatically invalidates React Query caches when entities change
 *
 * Must be mounted *inside* a company route (`BackofficeLayout`): the company is
 * read from the URL by `useCurrentClient`, and outside the router there is no
 * URL to read — it used to run in `App`, where it silently fell back on the
 * user's *first* company, so anyone with several companies listened to the
 * wrong room and every list (the call history first) stopped updating live.
 */
export function useClientSocket() {
  const currentClient = useCurrentClient();
  const queryClient = useQueryClient();
  const clientId = currentClient?.client?.id;

  useEffect(() => {
    if (!clientId) return;

    const room = `client/${clientId}`;

    // Join the client room
    websocketBus.emit("join", null, { room });

    // Listen for change notifications
    const handleChangeEvent = (event: ChangeEvent) => {
      if (event.type !== "changes") return;
      processEntityChanges(event, queryClient);
    };

    websocketBus.on(room, handleChangeEvent);

    // Switching company must stop the previous one's events from reaching
    // this one's caches.
    return () => {
      websocketBus.detach(room, handleChangeEvent);
    };
  }, [clientId, queryClient]);
}

/**
 * Handle entity changes by invalidating relevant React Query caches
 * Invalidates all queries starting with the table name
 */
function processEntityChanges(
  event: ChangeEvent,
  queryClient: ReturnType<typeof useQueryClient>,
) {
  try {
    const { t: tableName, ids, op } = event;

    console.log(
      `${op === "delete" ? "Deleted" : "Updated"} ${ids.length} record(s) in ${tableName}:`,
      ids,
    );

    // Invalidate all queries for this table
    queryClient.invalidateQueries({
      queryKey: [tableName],
    });

    console.log(`Cache invalidation completed for table: ${tableName}`);
  } catch (error) {
    console.error("Error handling database changes:", error);
  }
}
